import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { useLanguage } from '../../context/LanguageContext';
import { Percent, Calculator } from 'lucide-react';

const MaklarProvisionKalkylator = () => {
  const { language } = useLanguage();
  const [fastighetsPrice, setFastighetsPrice] = useState(5000000);
  const [provisionPercent, setProvisionPercent] = useState(4);
  const [result, setResult] = useState(null);

  const content = {
    sv: {
      title: 'Mäklarprovision kalkylator',
      description: 'Beräkna mäklarprovision vid försäljning av skogsfastighet. Vanlig provision för skogsfastigheter ligger på 3-5%.',
      propertyPrice: 'Fastighetspris (kr)',
      commissionRate: 'Provisionssats',
      calculate: 'Beräkna provision',
      result: 'Beräknad provision',
      commission: 'Mäklarprovision',
      netToSeller: 'Netto till säljare',
      priceAfterCommission: 'Efter moms (25%)',
      vatNote: 'Moms tillkommer på provisionen om säljaren är momspliktig',
      typicalRates: 'Vanliga provisionssatser: Fast arvode + 2-4% eller ren % (3-5%)'
    },
    de: {
      title: 'Maklerprovision Rechner',
      description: 'Berechnen Sie die Maklerprovision beim Verkauf einer Waldimmobilie.',
      propertyPrice: 'Immobilienpreis (SEK)',
      commissionRate: 'Provisionssatz',
      calculate: 'Provision berechnen',
      result: 'Berechnete Provision',
      commission: 'Maklerprovision',
      netToSeller: 'Netto an Verkäufer',
      priceAfterCommission: 'Nach MwSt. (25%)',
      vatNote: 'MwSt. wird auf die Provision erhoben',
      typicalRates: 'Übliche Provisionssätze: 3-5%'
    },
    en: {
      title: 'Broker Commission Calculator',
      description: 'Calculate broker commission for forest property sales.',
      propertyPrice: 'Property price (SEK)',
      commissionRate: 'Commission rate',
      calculate: 'Calculate commission',
      result: 'Calculated commission',
      commission: 'Broker commission',
      netToSeller: 'Net to seller',
      priceAfterCommission: 'After VAT (25%)',
      vatNote: 'VAT is added on the commission',
      typicalRates: 'Typical rates: 3-5%'
    },
    se: {
      title: 'Meklárprovišuvdna rehkenastin',
      description: 'Rehkenas meklárprovišuvnna meahcceopmodaga vuovdimis.',
      propertyPrice: 'Opmodathaddi (SEK)',
      commissionRate: 'Provišuvdnarievtti',
      calculate: 'Rehkenas provišuvnna',
      result: 'Rehkenastojuvvon provišuvdna',
      commission: 'Meklárprovišuvdna',
      netToSeller: 'Neahtta vuovdái',
      priceAfterCommission: 'MVA maŋŋá (25%)',
      vatNote: 'MVA lasihuvvo provišuvdnii',
      typicalRates: 'Dábálaš rievttit: 3-5%'
    }
  };

  const c = content[language] || content.sv;

  const calculateCommission = () => {
    const commission = fastighetsPrice * (provisionPercent / 100);
    const commissionWithVat = commission * 1.25;
    const netToSeller = fastighetsPrice - commissionWithVat;
    
    setResult({
      commission: Math.round(commission),
      commissionWithVat: Math.round(commissionWithVat),
      netToSeller: Math.round(netToSeller)
    });
  };

  return (
    <Card className="w-full" data-testid="maklar-provision-calculator">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-forest-700">
          <Percent className="h-5 w-5" />
          {c.title}
        </CardTitle>
        <CardDescription>{c.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{c.propertyPrice}</Label>
            <Input
              type="number"
              value={fastighetsPrice}
              onChange={(e) => setFastighetsPrice(Number(e.target.value))}
              min={0}
              step={100000}
              data-testid="price-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label>{c.commissionRate}: {provisionPercent}%</Label>
            <Slider
              value={[provisionPercent]}
              onValueChange={(v) => setProvisionPercent(v[0])}
              min={1}
              max={10}
              step={0.5}
              className="mt-2"
              data-testid="commission-slider"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1%</span>
              <span>5%</span>
              <span>10%</span>
            </div>
          </div>
        </div>

        <Button 
          onClick={calculateCommission} 
          className="w-full bg-forest-700 hover:bg-forest-800"
          data-testid="calculate-btn"
        >
          <Calculator className="mr-2 h-4 w-4" />
          {c.calculate}
        </Button>

        {result && (
          <div className="mt-4 p-4 bg-forest-50 rounded-lg border border-forest-200" data-testid="result">
            <h4 className="font-semibold text-forest-800 mb-3">{c.result}</h4>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center pb-2 border-b border-forest-200">
                <span className="text-sm text-muted-foreground">{c.commission} ({provisionPercent}%)</span>
                <span className="text-lg font-semibold text-forest-700">{result.commission.toLocaleString('sv-SE')} kr</span>
              </div>
              
              <div className="flex justify-between items-center pb-2 border-b border-forest-200">
                <span className="text-sm text-muted-foreground">{c.priceAfterCommission}</span>
                <span className="text-lg font-semibold text-amber-600">{result.commissionWithVat.toLocaleString('sv-SE')} kr</span>
              </div>
              
              <div className="flex justify-between items-center pt-2">
                <span className="font-medium text-forest-800">{c.netToSeller}</span>
                <span className="text-2xl font-bold text-forest-700">{result.netToSeller.toLocaleString('sv-SE')} kr</span>
              </div>
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground">{c.vatNote}</p>
        <p className="text-xs text-muted-foreground">{c.typicalRates}</p>
      </CardContent>
    </Card>
  );
};

export default MaklarProvisionKalkylator;
